import {useEffect, useReducer} from 'react';
import {useApolloClient} from '@apollo/client';
import {ApptProps} from 'components/ApptDetails2';
import {Routes} from 'constants/index';
import {navigate} from 'gatsby';
import {Auth, usePermissions} from 'hooks/usePermissions';
import {omit} from 'lodash';
import {createRedirect} from 'utils/auth';

import {FormValues} from './Form';
import {useReserve} from './mutation.reserve';
import {useAppt} from './query.appt';

type State = {
	appt?: ApptProps
	auth?: Auth
	step:
		| 'LOADING'
		| 'CREATE'
		| 'CONFIRM'
		| 'LOG-IN'
		| 'ERROR'
	error?: string
}

type Action =
  | { type: 'SET-APPT', appt: ApptProps }
  | { type: 'SET-AUTH', auth: Auth }
  | { type: 'LOG-IN' }
  | { type: 'ERROR', error: string }
;

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
	case 'SET-APPT':
	case 'SET-AUTH':
		state = {...state, ...omit(action, 'type')};
		if (!state.appt || !state.auth) {
			return state;
		} else if (state.auth.isValidUser()) {
			return {
				...state,
				step: 'CONFIRM',
			};
		} else {
			return {
				...state,
				step: 'CREATE',
			};
		}

	case 'LOG-IN':
		return {
			...state,
			step: 'LOG-IN',
		};

	case 'ERROR':
		return {
			...state,
			error: action.error,
			step: 'ERROR',
		};
	}
};

const wrapError = (error: string): Action => ({
	type: 'ERROR',
	error: error + ' Please refresh the page and try again.',
});

export const useState = (props: {
	partner: string,
	appt: string,
}) => {
	// Init various helper state hooks
	const appt = useAppt({appt: props.appt});
	const client = useApolloClient();
	const permissions = usePermissions();

	// Create state & reducer/dispatch
	const [state, dispatch] = useReducer(reducer, {
		step: 'LOADING',
	});

	// Set up API handler
	const [reserve, {loading}] = useReserve({
		onCompleted: async (data) => {
			const {error} = data.payment.reservePartner;
			if (error === 'User with email already exists.') {
				dispatch({type: 'LOG-IN'});
			} else if (error) {
				dispatch(wrapError(error));
			} else {
				// TODO: come up with a better final outcome?
				if (!state.auth?.isValidUser()) {
					await client.resetStore();
				}
				navigate(Routes.bookingSuccess);
			}
		},
		onError: () => {
			dispatch(wrapError('Encountered a network error.'));
		}
	});

	// Set appt after API returns
	useEffect(() => {
		if (appt.loading || !appt.appt || state.appt) {
			return;
		} else {
			dispatch({type: 'SET-APPT', appt: appt.appt});
		}
	}, [appt.loading]);

	// Set auth after API returns
	useEffect(() => {
		if (permissions.loading || state.auth) {
			return;
		} else {
			dispatch({type: 'SET-AUTH', auth: permissions.auth});
		}
	}, [permissions.loading]);

	return {
		state,
		creating: loading,
		onClick: (values: unknown) => {
			switch (state.step) {
			case 'CREATE':
				reserve({
					variables: {
						...props,
						create: values as FormValues,
					}
				});
				return;

			case 'CONFIRM':
				reserve({variables: {...props}});
				return;

			case 'LOG-IN':
				navigate(createRedirect(Routes.signIn));
				return;
			}
		},
	};
};
